<template>
  <div class="auth-widget-container">
    <div v-if="methodsToShow.length > 1 && mode === methodsToShow.join(',')">
      <div class="method-selector">
        <div>Selecciona un método</div>
        <div class="methods">
          <div
            v-if="isMethodShowable(AuthMethods.QUESTIONS)"
            v-bind:class="{ active: isQuestions }"
            class="option"
            @click="mode = AuthMethods.QUESTIONS"
          >
            Desafio
          </div>
          <div
            v-if="isMethodShowable(AuthMethods.FACIALRECOGNITION)"
            v-bind:class="{ active: isFacialRecognition }"
            class="option"
            @click="mode = AuthMethods.FACIALRECOGNITION"
          >
            Biometría facial
          </div>
        </div>
      </div>
    </div>

    <div v-if="isQuestions">
      <div class="widget">
        <QuestionAuthentication
          :company="company"
          :rut="rut.toUpperCase()"
          :color="color"
          :question-service="questionService"
          :has-go-back="_events.goBack !== undefined"
          :trace-client="traceClient"
          :skip-first-screen="skipFirstScreen"
          :custom-errors="customErrors"
          :provider="provider"
          :timeLimitMode="timeLimitMode"
          @processAborted="(data) => $emit('processAborted', data)"
          @goBack="$emit('goBack')"
          @challengeAuth="$emit('auth', $event)"
          :success-callback="successCallback"
          :failure-callback="failureCallback"
          :portal-id="portalId"
          :channel-key="channelKey"
        />
      </div>
    </div>
    <FacialRecognition
      v-if="isFacialRecognition"
      :transactionId="transactionId"
      :company="company"
      :dummyMode="dummyMode"
      :kong-client="kongClient"
      :wrapper-url="facialRecognitionWrapperUrl"
      :facial-recognition-service="facialRecognitionService"
      :deviceInfoService="authDeviceInfoService"
      :successCallback="successCallback"
      :failureCallback="failureCallback"
      @result="$emit('auth', $event)"
      @goBack="$emit('goBack')"
      :trace-id="traceId"
      :rut="rut"
      :config="config"
      :light-mode="lightMode"
      :facial-timer="facialTimer"
      :kongModeJWT="kongModeJWT"
      :portal-id="portalId"
      :retry-button="retryButton"
      :retries-number="retriesNumber"
      :dummy-result="dummyResult"
      :dummy-response-time-in-seconds="dummyResponseTimeInSeconds"
      :mobile-flow="mobileFlow"
      :channel-key="channelKey"
    />
    <UniqueStatePassword
      v-if="isCue"
      :company="company"
      :rut="rut"
      :dummyMode="dummyMode"
      :cue-service="cueService"
      :portal-id="portalId"
      :channel-key="channelKey"
      :successCallback="successCallback"
      :failureCallback="failureCallback"
      :autostart="autostart"
      @result="$emit('auth', $event)"
    />
    <AuthPortal
      v-if="isAuthPortal"
      :portalService="portalService"
      :rut="rut"
      :company="company"
      :duration-in-hours="portalDurationInHours"
      :validation-methods="portalValidationMethods"
      :validation-purpose="portalValidationPurpose"
      :channel-key="channelKey"
      :trace-id="traceId"
      @result="$emit('auth', $event)"
    />
    <EcertValidation
      v-if="isEcertValidation"
      :ecertService="ecertService"
      :rut="rut"
      :name="name"
      :lastName="clientLastName"
      :motherLastName="clientMotherLastName"
      :email="clientEmail"
      :dummyMode="dummyMode"
      :portal-id="portalId"
      :channel-key="channelKey"
      :trace-client="traceClient"
      @result="$emit('auth', $event)"
      @goBack="$emit('goBack')"
    />
  </div>
</template>

<script>
import Config from "@/utils/config";

import {
  AuthDeviceInfoService,
  CueService,
  DummyAuthDeviceInfoService,
  DummyCueService,
  DummyFacialRecognitionService,
  DummyQuestionService,
  DummyTraceService,
  DummyAuthOtiService,
  EcertService,
  FacialRecognitionService,
  KongService,
  QuestionService,
  TraceService,
  AuthOtiService,
  DummyPortalService,
  PortalService,
  DummyEcertService,
} from "fulfillment-commons-js";

import QuestionAuthentication from "@/components/QuestionAuthentication";
import FacialRecognition from "@/components/FacialRecognition";
import UniqueStatePassword from "@/components/UniqueStatePassword";
import AuthPortal from "@/components/AuthPortalCode";
import EcertValidation from "@/components/EcertValidation";

const AuthMethods = {
  QUESTIONS: "questions",
  FACIALRECOGNITION: "facialRecognition",
  UNIQUESTATEPASSWORD: "uniqueStatePassword",
  AUTHPORTAL: "authPortal",
  ECERTADVANCEDSIGN: "ecertAdvancedSign"
};

export default {
  name: "AuthWidget",
  components: {
    QuestionAuthentication,
    FacialRecognition,
    UniqueStatePassword,
    AuthPortal,
    EcertValidation
  },
  props: {
    method: { type: String, default: AuthMethods.FACIALRECOGNITION },
    rut: { type: String },
    name: { type: String },
    lastName: { type: String },
    motherLastName: { type: String },
    email: { type: String },
    executiveRut: { type: String },
    executiveName: { type: String },
    traceId: { type: String, default: TraceService.createTraceId() },
    autostart: { type: Boolean, default: false },
    company: { type: String, default: "cuprum" },
    onlyButton: { type: Boolean, default: false },
    buttonLabel: { type: String, default: "Validar" },
    buttonColor: { type: String },
    buttonBackground: { type: String },
    buttonWidth: { type: String },
    buttonEnabled: { type: Boolean, default: true },
    kongClientId: { type: String },
    kongClientSecret: { type: String },
    kongToken: { type: String },
    dummyMode: { type: Boolean, default: false },
    otiMode: { type: Boolean, default: false },
    color: { type: String, default: "#0091DA" },
    environment: { type: String, default: Config.buildEnvironment() },
    skipFirstScreen: { type: Boolean, default: false },
    kongModeJWT: { type: Boolean, default: false },
    customErrors: { type: Object },
    provider: { type: String },
    timeLimitMode: { type: String },
    transactionId: { type: String, default: "" },
    successCallback: { type: Function },
    failureCallback: { type: Function },
    lightMode: { type: Boolean, default: false },
    facialTimer: { type: Number, default: 3 },
    portalId: { type: String },
    retryButton: { type: Boolean, default: false },
    retriesNumber: { type: Number, default: 3 },
    dummyResult: { type: String },
    dummyResponseTimeInSeconds: { type: Number, default: 3 },
    mobileFlow: { type: Boolean, default: false },
    channelKey: { type: String },
    portalDurationInHours: { type: Number },
    portalValidationMethods: { type: String },
    portalValidationPurpose: { type: String },
  },
  data() {
    return {
      mode: this.method,
      config: new Config(this.environment),
    };
  },
  created() {
    this.config.printInfo();
  },
  computed: {
    clientEmail() {
      return this.email;
    },
    clientLastName() {
      return this.lastName;
    },
    clientMotherLastName() {
      return this.motherLastName;
    },
    isQuestions() {
      return this.mode === AuthMethods.QUESTIONS;
    },
    isFacialRecognition() {
      return this.mode === AuthMethods.FACIALRECOGNITION;
    },
    isCue() {
      return this.mode === AuthMethods.UNIQUESTATEPASSWORD;
    },
    isAuthPortal() {
      return this.mode === AuthMethods.AUTHPORTAL;
    },
    isEcertValidation() {
      return this.mode === AuthMethods.ECERTADVANCEDSIGN;
    },
    AuthMethods() {
      return AuthMethods;
    },
    methodsToShow() {
      return this.method.split(",").filter((val) => val !== "");
    },
    kongClient() {
      if (this.dummyMode) {
        return KongService.fromToken("devDummyModeToken");
      } else if (this.kongToken) {
        return KongService.fromToken(this.kongToken);
      } else if (this.kongClientId && this.kongClientSecret) {
        return KongService.fromClientCredentials(
          this.config.kongUrl(this.company),
          this.kongClientId,
          this.kongClientSecret
        );
      } else {
        throw new Error("Missing parameter: either kong token or kong token credentials must be defined, found none");
      }
    },
    traceClient() {
      if (this.dummyMode) {
        return new DummyTraceService();
      } else {
        let operationBase = this.method.charAt(0).toUpperCase() + this.method.substring(1).toLowerCase();
        return new TraceService(this.kongClient, this.config.kafkaRestUrl(this.kongModeJWT), {
          traceId: this.traceId,
          channel: "auth-widget",
          company: this.company,
          operation: `${operationBase}`,
          clientId: this.clientRut,
          advisorId: this.executiveRut,
        });
      }
    },
    authApiClient() {
      if (this.dummyMode) {
        return new DummyAuthOtiService();
      } else {
        return new AuthOtiService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrl(this.kongModeJWT),
          this.config.authApiUrlPFG(this.kongModeJWT),
          this.otiMode
        );
      }
    },
    questionService() {
      if (this.dummyMode) {
        return new DummyQuestionService(this.company, this.rut);
      } else {
        return new QuestionService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrl(this.kongModeJWT),
          this.config.authApiUrlPFG(this.kongModeJWT)
        );
      }
    },
    facialRecognitionService() {
      if (this.dummyMode) {
        return new DummyFacialRecognitionService();
      } else {
        return new FacialRecognitionService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrl(this.kongModeJWT),
          this.config.authApiUrlPFG(this.kongModeJWT)
        );
      }
    },
    cueService() {
      if (this.dummyMode) {
        return new DummyCueService();
      } else {
        return new CueService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrl(this.kongModeJWT),
          this.config.authApiUrlPFG(this.kongModeJWT)
        );
      }
    },
    ecertService() {
      if (this.dummyMode) {
        return new DummyEcertService();
      } else {
        return new EcertService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrlPFG(this.kongModeJWT)
        );
      }
    },
    portalService() {
      if (this.dummyMode) {
        return new DummyPortalService();
      } else {
        return new PortalService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrl(this.kongModeJWT),
          this.config.authApiUrlPFG(this.kongModeJWT)
        );
      }
    },
    facialRecognitionWrapperUrl() {
      if (Config.isDevServer()) {
        return `${window.location.origin}/facial-recognition.html#/`;
      }
      return this.config.wrapperUrl(this.company);
    },
    authDeviceInfoService() {
      if (this.dummyMode) {
        return new DummyAuthDeviceInfoService();
      } else {
        return new AuthDeviceInfoService(
          this.kongClient,
          this.traceClient,
          this.config.authApiUrl(this.kongModeJWT),
          this.config.authApiUrlPFG(this.kongModeJWT)
        );
      }
    },
  },
  methods: {
    isMethodShowable(method) {
      return this.methodsToShow.indexOf(method) !== -1;
    },
  },
  watch: {
    method(value) {
      this.mode = value;
    },
  },
};
</script>

<style scoped>
.auth-widget-container {
  height: 100%;
}

.method-selector {
  clear: both;
  text-align: center;
}

.methods {
  display: table;
  margin: auto;
}

.option {
  border: 2px solid #555;
  border-radius: 10px;
  margin: 10px;
  text-align: center;
  vertical-align: middle;
  padding: 16px;
  cursor: pointer;
  display: inline-block;
  min-width: 230px;
}

.option:hover {
  background-color: #f1f1f1;
}

@media screen and (max-width: 480px) {
  .option,
  .methods {
    display: block;
  }
}

.option.active {
  background-color: #bfbfbf;
}
</style>
